import type { CatalogItem } from '@viva/core';
import type { MediaTrack, PlayerStatus } from '@viva/player';
import { PlaybackController, playbackCandidates, WebVideoEngine } from '@viva/player';
import type { RefObject } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { SubtitleConversion, VideoConversion } from '../../desktop-media';
import {
  afterProbe,
  audioConversionAvailable,
  conversionPossible,
  hevcPlayable,
  isDesktopApp,
  needsAudioConversion,
  needsRemux,
  probeMedia,
  shouldProbe,
  subtitlesAvailable,
  transcodeUrl,
  videoConversionAvailable,
} from '../../desktop-media';
import { currentRelay, providerUrl } from '../../network';
import { uhdCutByRelay } from '../../uhd-access';
import { audioIsDecoding, bufferedEnd } from './media-element';
import { useConversionSubtitles } from './subtitles';
import { conversionRestartAt, headStartFor } from './conversion-restart';

/** First picture through the relay: conversion start (2–4 s) plus the tunnel's slow moments. */
const RELAY_START_TIMEOUT_MS = 30_000;

/** Seconds of playback after which a stream without decoded audio is acted on. */
const NO_AUDIO_AFTER_S = 4;

/** Playback through the desktop app's ffmpeg audio conversion. */
export interface Conversion {
  url: string;
  live: boolean;
  /** Source position the converted stream starts at. */
  offset: number;
  /** Full length of the source, when known. */
  duration: number | null;
  /** Original audio codec, or `null` when detected only by silence. */
  codec: string | null;
  /** Seconds the relay sends ahead (#242): less for 4K. Default: the relay's own. */
  headStart?: number;
  /** 4K HEVC in a browser: the picture is converted to 1080p H.264 too. */
  video?: VideoConversion;
  /** Browser version: the file's text subtitles, written during the conversion. */
  subtitles?: SubtitleConversion;
  /** 4K HEVC picture passed on unchanged, for a device that decodes it (#224). */
  hevc?: true;
}

function newSubtitleSession(): string {
  return Array.from(crypto.getRandomValues(new Uint8Array(12)), (byte) => byte.toString(16).padStart(2, '0')).join('');
}

export interface Timeline {
  position: number;
  duration: number;
  bufferedEnd: number;
}

/**
 * One player session per overlay: owns the controller, starts each item
 * (probing and converting audio on desktop when needed), and exposes a
 * timeline and seeking that stay correct during conversion. `blocked`: a 4K/8K title this device
 * can't play, or 4K stopped on the relay (#301); it isn't opened, the overlay offers the HD version.
 */
export function usePlaybackSession(videoRef: RefObject<HTMLVideoElement | null>, item: CatalogItem, ready = true, blocked = false) {
  const controllerRef = useRef<PlaybackController | null>(null);
  const conversionRef = useRef<Conversion | null>(null);
  const [status, setStatus] = useState<PlayerStatus>({ state: 'idle' });
  const [firstFrameMs, setFirstFrameMs] = useState<number | null>(null);
  const [tracks, setTracks] = useState<{ audio: MediaTrack[]; text: MediaTrack[] }>({ audio: [], text: [] });
  const [time, setTime] = useState<Timeline>({ position: 0, duration: 0, bufferedEnd: 0 });
  const [conversion, setConversionState] = useState<Conversion | null>(null);
  const [probing, setProbing] = useState(false);
  const [noAudio, setNoAudio] = useState(false);
  /** A 4K/UHD title in HEVC that this browser can't decode. */
  const [unsupportedVideo, setUnsupportedVideo] = useState(false);
  /** The relay could open nothing at the movie's address; Retry asks again (#190). */
  const [openFailed, setOpenFailed] = useState(false);
  const [openAttempt, setOpenAttempt] = useState(0);
  const retryOpen = useCallback(() => setOpenAttempt((attempt) => attempt + 1), []);

  // One engine and controller for the lifetime of the overlay.
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return undefined;
    // Through the relay (browser version) every chunk takes a detour, and movies may be converted
    // first: buffer more to ride out slow moments, and allow longer for the first picture.
    const viaRelay = currentRelay() !== null;
    const controller = new PlaybackController(new WebVideoEngine(video, { requestUrl: providerUrl, largeBuffers: viaRelay }), {
      startTimeoutMs: viaRelay ? RELAY_START_TIMEOUT_MS : undefined,
      isVisible: () => document.visibilityState === 'visible',
    });
    controllerRef.current = controller;
    const unsubscribe = controller.subscribe((next) => {
      setStatus(next);
      setFirstFrameMs(controller.metrics.firstFrameMs);
      setTracks({ audio: controller.audioTracks(), text: controller.textTracks() });
    });
    return () => {
      unsubscribe();
      controller.destroy();
      controllerRef.current = null;
    };
  }, [videoRef]);

  const setConversion = useCallback((next: Conversion | null) => {
    conversionRef.current = next;
    setConversionState(next);
  }, []);

  /** Where the viewer is in a converted movie (source time), and when its conversion was restarted there. */
  const watchedAt = useRef(0);
  const conversionRestarts = useRef<number[]>([]);

  const startConversion = useCallback(
    (next: Conversion) => {
      // Each conversion (start, seek) writes its own subtitle files, timed from where it starts.
      const withSession: Conversion = next.subtitles ? { ...next, subtitles: { ...next.subtitles, session: newSubtitleSession() } } : next;
      setConversion(withSession);
      watchedAt.current = withSession.offset;
      const url = transcodeUrl(withSession.url, withSession.offset, withSession.video, withSession.subtitles);
      const burst = withSession.headStart !== undefined && url.includes('/transcode?') ? `&burst=${withSession.headStart}` : '';
      controllerRef.current?.play([{ url: url + burst, kind: 'ts' }], { live: withSession.live });
    },
    [setConversion],
  );
  useConversionSubtitles(videoRef, conversion?.subtitles);

  // Start (or zap to) the item.
  useEffect(() => {
    const controller = controllerRef.current;
    const url = item.url;
    if (!controller || url === null) return undefined;
    // Waiting for a stream slot (trial pool queue): nothing plays yet.
    if (!ready) {
      controller.stop();
      setOpenFailed(false);
      return undefined;
    }
    const live = item.kind === 'live';
    const abort = new AbortController();
    setConversion(null);
    setNoAudio(false);
    setUnsupportedVideo(false);
    setOpenFailed(false);
    if (blocked) {
      controller.stop();
      setUnsupportedVideo(true);
      return undefined;
    }
    const playDirect = () => controller.play(playbackCandidates(url, live), { live });
    if (live || !conversionPossible() || !shouldProbe(url)) {
      playDirect();
      return () => abort.abort();
    }
    // VOD in containers that often carry Dolby/DTS (and, in browsers, MKV/AVI they can't open): check first.
    controller.stop();
    setProbing(true);
    void (async () => {
      const [available, probed, videoAvailable, subtitlesOn] = await Promise.all([
        audioConversionAvailable(),
        probeMedia(url, abort.signal),
        videoConversionAvailable(),
        subtitlesAvailable(),
      ]);
      if (abort.signal.aborted) return;
      setProbing(false);
      const probe = afterProbe(probed, url);
      if (probe === 'fail') {
        setOpenFailed(true);
        return;
      }
      const textTracks = subtitlesOn ? (probe?.subtitles ?? []).filter((track) => track.text) : [];
      const subtitles = textTracks.length > 0 ? { session: '', tracks: textTracks } : undefined;
      // 4K titles (HEVC, 25–60 Mbit/s through the relay): played as they are where the device decodes
      // HEVC (Firefox claims it and fails, so it never counts), else turned into 1080p H.264 by a relay
      // that can. Without either, the player opens the HD/LQ version instead (#224).
      if (probe?.videoCodec === 'hevc' && !isDesktopApp()) {
        // The admin stopped 4K through the relay (#301): the overlay looks for the HD version.
        if (uhdCutByRelay()) {
          setUnsupportedVideo(true);
          return;
        }
        // This device decodes HEVC: the relay passes the 4K picture on and only converts the sound.
        if (available && hevcPlayable()) {
          startConversion({ url, live: false, offset: 0, duration: probe.durationSeconds, codec: needsAudioConversion(probe) ? (probe.audioCodecs[0] ?? null) : null, subtitles, hevc: true, headStart: headStartFor(probe.height) });
          return;
        }
        if (!videoAvailable) {
          setUnsupportedVideo(true);
          return;
        }
        const video = { height: probe.height ?? null, hdr: probe.hdr === true };
        startConversion({ url, live: false, offset: 0, duration: probe.durationSeconds, codec: probe.audioCodecs[0] ?? null, video, subtitles });
        return;
      }
      // In browsers an MKV/AVI file can't play as it is: always convert it.
      const remux = needsRemux(url);
      if (remux || (available && probe && needsAudioConversion(probe))) {
        startConversion({ url, live: false, offset: 0, duration: probe?.durationSeconds ?? null, codec: probe && needsAudioConversion(probe) ? (probe.audioCodecs[0] ?? null) : null, subtitles, headStart: headStartFor(probe?.height) });
      } else {
        playDirect();
      }
    })();
    return () => {
      abort.abort();
      setProbing(false);
    };
  }, [item, ready, blocked, setConversion, startConversion, openAttempt]);

  // Timeline in source time (conversion streams start at their offset).
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return undefined;
    const update = () => {
      const active = conversionRef.current;
      const offset = active && !active.live ? active.offset : 0;
      if (active && !active.live && !video.seeking && video.readyState >= 2 && video.currentTime > 0) watchedAt.current = offset + video.currentTime;
      setTime({
        position: offset + video.currentTime,
        duration: active && !active.live ? (active.duration ?? Number.NaN) : video.duration,
        bufferedEnd: offset + bufferedEnd(video),
      });
    };
    const events = ['timeupdate', 'durationchange', 'progress', 'seeking'];
    events.forEach((name) => video.addEventListener(name, update));
    return () => events.forEach((name) => video.removeEventListener(name, update));
  }, [videoRef]);

  // A converted movie that reconnects goes on from where the viewer was, not from where this
  // conversion began (the start in its address) (#242).
  useEffect(() => {
    watchedAt.current = 0;
    conversionRestarts.current = [];
  }, [item]);
  useEffect(() => {
    const active = conversionRef.current;
    if (!active) return;
    const now = Date.now();
    const at = conversionRestartAt({
      reconnecting: status.state === 'reconnecting',
      dropped: status.state === 'reconnecting' && status.dropped,
      live: active.live,
      offset: active.offset,
      watchedAt: watchedAt.current,
      restarts: conversionRestarts.current,
      now,
    });
    if (at === null) return;
    conversionRestarts.current = [...conversionRestarts.current, now];
    startConversion({ ...active, offset: at });
  }, [status, startConversion]);

  // Silent playback: convert on desktop, otherwise tell the viewer.
  useEffect(() => {
    if (conversion) return undefined;
    const timer = setInterval(() => {
      const video = videoRef.current;
      const url = item.url;
      if (!video || url === null || video.paused || video.currentTime < NO_AUDIO_AFTER_S) return;
      if (audioIsDecoding(video) !== false) return;
      clearInterval(timer);
      void audioConversionAvailable().then((available) => {
        if (!available) {
          setNoAudio(true);
          return;
        }
        const live = item.kind === 'live';
        startConversion({
          url,
          live,
          offset: live ? 0 : video.currentTime,
          duration: Number.isFinite(video.duration) ? video.duration : null,
          codec: null,
        });
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [item, conversion, startConversion, videoRef]);

  // 4K passed on unchanged but the device can't play it after all: offer the other versions instead.
  useEffect(() => {
    if (conversion?.hevc && status.state === 'error') setUnsupportedVideo(true);
  }, [conversion, status.state]);

  // A format the browser rejects outright (e.g. Dolby audio in a TS stream)
  // never plays at all, so silence detection cannot catch it. On desktop,
  // switch to audio conversion as soon as the rejection happens, before the
  // controller moves on to alternate URLs.
  const rejected = controllerRef.current?.metrics.problems.some((problem) => problem.error.code === 'unsupported') ?? false;
  useEffect(() => {
    const url = item.url;
    if (!rejected || conversionRef.current || url === null) return;
    void audioConversionAvailable().then((available) => {
      if (!available || conversionRef.current) return;
      const live = item.kind === 'live';
      startConversion({ url, live, offset: 0, duration: null, codec: null });
    });
  }, [rejected, item, startConversion]);

  const position = useCallback(() => {
    const active = conversionRef.current;
    return (active && !active.live ? active.offset : 0) + (videoRef.current?.currentTime ?? 0);
  }, [videoRef]);

  const seekTo = useCallback(
    (seconds: number) => {
      const active = conversionRef.current;
      if (active && !active.live) {
        const limit = active.duration !== null ? Math.max(0, active.duration - 1) : Number.POSITIVE_INFINITY;
        startConversion({ ...active, offset: Math.min(limit, Math.max(0, seconds)) });
      } else {
        controllerRef.current?.seek(seconds);
      }
    },
    [startConversion],
  );

  const seekBy = useCallback((delta: number) => seekTo(position() + delta), [seekTo, position]);

  // Paused live TV: how far behind the broadcast playback is (shown with a "Go live" button).
  const [behindLive, setBehindLive] = useState(0);
  useEffect(() => {
    if (item.kind !== 'live') return undefined;
    setBehindLive(0);
    const timer = setInterval(() => setBehindLive(Math.round(controllerRef.current?.behindLive() ?? 0)), 1000);
    return () => clearInterval(timer);
  }, [item]);

  // Back on screen (unlocked, back from another app): live TV the browser paused goes live again.
  useEffect(() => {
    if (item.kind !== 'live') return undefined;
    const onVisible = () => {
      if (document.visibilityState === 'visible') controllerRef.current?.returnToLive();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [item]);

  const goLive = useCallback(() => {
    controllerRef.current?.goLive();
    setBehindLive(0);
  }, []);

  const unmute = useCallback(() => controllerRef.current?.unmute(), []);

  const togglePause = useCallback(() => {
    const controller = controllerRef.current;
    if (!controller) return;
    if (controller.status.state === 'ended') seekTo(0);
    else if (controller.status.state === 'paused') controller.resume();
    else controller.pause();
  }, [seekTo]);

  return { controllerRef, status, firstFrameMs, tracks, time, conversion, probing, noAudio, unsupportedVideo, openFailed, retryOpen, behindLive, goLive, seekTo, seekBy, togglePause, unmute };
}
